/**
 * Passo 2 di "Scrivi a piu' persone": da quale numero e che cosa.
 *  - Numero Ufficiale: solo modelli approvati (fuori dalle 24 ore Meta accetta solo quelli).
 *  - Numero Standard: testo libero con {{nome}}, con l'avviso sul rischio di blocco.
 */
import { AlertTriangle, Info } from 'lucide-react';
import { etichettaNumero } from '@/lib/services/whatsapp/phoneDigits';
import { BULK_LIMITS } from '@/lib/services/whatsapp/bulkSend/bulkSchedule';
import { NAME_TOKEN, MAX_TEXT_LENGTH } from '@/lib/services/whatsapp/bulkSend/bulkMessage';
import { headerNeedsMedia, HEADER_MEDIA_LINK_KEY } from '@/lib/services/whatsapp/templateSend';
import { tip } from '@/components/messages/ChatTooltip';
import type { WhatsAppSessionConfig } from '@/lib/hooks/useWhatsAppConfigs';
import type { BulkSendState } from './useBulkSend';

const inputCls = 'w-full px-2.5 py-1.5 text-sm rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/10 bg-white dark:bg-[#1f1f22] text-[#37352F] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2383E2]/30';

const configLabel = (c: WhatsAppSessionConfig) => {
  const numero = etichettaNumero(c.phoneNumber);
  const tipo = c.serviceName === 'waha' ? 'Standard' : 'Ufficiale';
  return `${c.displayName || numero || 'WhatsApp'}${c.displayName && numero ? ` (${numero})` : ''} - ${tipo}`;
};

export function BulkMessageStep({ s }: { s: BulkSendState }) {
  const lim = BULK_LIMITS[s.channel];
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-[#787774] mb-1">Invia dal numero</label>
        <select className={inputCls} value={s.configId ?? ''} onChange={(e) => s.setConfigId(e.target.value)} aria-label="Numero da cui inviare">
          {s.configs.map((c) => <option key={c.id} value={c.id}>{configLabel(c)}</option>)}
        </select>
        <p className="mt-1 text-xs text-[#787774]">
          {s.built.recipients.length} destinatari con questo numero
          {s.built.excluded.length > 0 ? `, ${s.built.excluded.length} esclusi` : ''}.
        </p>
      </div>

      {s.channel === 'standard' ? (
        <>
          <div className="flex gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-200">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold">Rischio di blocco del numero</p>
              <p>
                Un numero Standard che manda lo stesso messaggio a molte persone puo' essere segnalato e bloccato da WhatsApp,
                anche per sempre. Per ridurre il rischio: al massimo {lim.max} persone per invio, una pausa casuale di
                {' '}{lim.minGapSec}-{lim.maxGapSec} secondi fra un messaggio e l'altro, solo a chi conosci gia'.
                Scrivi un messaggio personale, con {NAME_TOKEN}, e non ripetere invii simili nello stesso giorno.
              </p>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-[#787774]">Messaggio</label>
              <button
                type="button"
                onClick={() => s.setText(`${s.text}${s.text.endsWith(' ') || !s.text ? '' : ' '}${NAME_TOKEN}`)}
                className="text-xs text-[#2383E2] hover:underline"
                {...tip('Aggiunge il nome del Lead o Cliente, diverso per ciascuno')}
              >
                Inserisci {NAME_TOKEN}
              </button>
            </div>
            <textarea
              className={`${inputCls} min-h-[140px] resize-y`}
              value={s.text}
              maxLength={MAX_TEXT_LENGTH}
              onChange={(e) => s.setText(e.target.value)}
              placeholder={`Ciao ${NAME_TOKEN}, ...`}
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex gap-2 rounded-lg bg-[#2383E2]/5 border border-[#2383E2]/20 p-3 text-xs text-[#37352F] dark:text-white/80">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#2383E2]" />
            <p>
              Dal numero Ufficiale, a chi non ti ha scritto nelle ultime 24 ore si possono mandare solo modelli approvati da Meta.
              Nei campi puoi scrivere {NAME_TOKEN}: ognuno riceve il suo nome.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#787774] mb-1">Modello approvato</label>
            <select className={inputCls} value={s.templateId} onChange={(e) => s.pickTemplate(e.target.value)} aria-label="Modello approvato">
              <option value="">{s.templates.length === 0 ? 'Nessun modello approvato su questo numero' : 'Scegli il modello'}</option>
              {s.templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.language})</option>)}
            </select>
          </div>
          {s.shape && (
            <div className="space-y-2">
              {s.shape.bodyText && (
                <p className="text-xs text-[#787774] whitespace-pre-wrap rounded-md bg-[rgba(55,53,47,0.04)] dark:bg-white/5 p-2">{s.shape.bodyText}</p>
              )}
              {headerNeedsMedia(s.shape) && (
                <input
                  type="url"
                  className={inputCls}
                  value={s.vars[HEADER_MEDIA_LINK_KEY] || ''}
                  onChange={(e) => s.setVars({ ...s.vars, [HEADER_MEDIA_LINK_KEY]: e.target.value })}
                  placeholder="Link https all'allegato dell'intestazione"
                  aria-label="Allegato dell'intestazione"
                />
              )}
              {s.shape.fields.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="text-xs font-mono text-[#787774] min-w-16 max-w-[40%] truncate" {...tip(f.label)}>{f.label}</span>
                  <input
                    type="text"
                    className={inputCls}
                    value={s.vars[f.key] ?? ''}
                    onChange={(e) => s.setVars({ ...s.vars, [f.key]: e.target.value })}
                    placeholder="(vuoto)"
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
